import firebase from "firebase/app";
import { useTranslation } from "next-i18next";
import NextLink from "next/link";
import React, { useState } from "react";

import { AnalyticsEventEnum } from "@/consts/AnalyticsEventEnum";
import { OutlinedButton } from "@/features/common/components/button/OutlinedButton";
import Heading from "@/features/common/components/tailwind/Heading";
import { Label } from "@/features/common/components/tailwind/Label";
import {
  RadioGroup,
  RadioGroupItem,
} from "@/features/common/components/tailwind/RadioGroup";
import { useFeatureEnabled } from "@/features/common/hooks/useFeatureEnabled";
import { JoinSubscriptionBoxView } from "@/features/subscription/components/joinSubscriptionDialog/JoinSubscriptionBoxView";
import { JoinSubscriptionPaymentButton } from "@/features/subscription/components/joinSubscriptionDialog/JoinSubscriptionPaymentButton";
import {
  JoinSubscriptionConfirmPaymentButtonNewProps,
  PaymentMethodTypeEnum,
} from "@/features/subscription/components/joinSubscriptionDialog/type";
import { useUserContext } from "@/features/user/contexts/useUserContext";
import { FeatureNameEnum } from "@/generated/generated-hooks";

import { JoinSubscriptionByCreditView } from "./offlinePayment/JoinSubscriptionByCreditView";
import { JoinSubscriptionByStripeView } from "./stripe/JoinSubscriptionByStripeView";

type JoinSubscriptionPaymentViewProps =
  JoinSubscriptionConfirmPaymentButtonNewProps & {
    onCancel?: () => void;
    isUnlockExpiredPost?: boolean;
    postId?: string;
    price: number;
  };

export const JoinSubscriptionPaymentView: React.FC<JoinSubscriptionPaymentViewProps> =
  ({ subscriptionPlanId, onCancel, isUnlockExpiredPost, postId, price }) => {
    const { user } = useUserContext();
    const { t } = useTranslation(["subscriptionPlan", "common"]);
    const { isEnabled } = useFeatureEnabled();
    const isOfflinePaymentEnabled = isEnabled(FeatureNameEnum.OfflinePayment);
    // @todo: check user.creditBalance >= price to set default payment method to offline payment
    const [payAs, setPayAs] = useState<PaymentMethodTypeEnum>(
      PaymentMethodTypeEnum.CreditCard,
    );
    const showDefault = payAs === PaymentMethodTypeEnum.CreditCard;

    const onClickCreditCard = () => {
      firebase
        .analytics()
        .logEvent(AnalyticsEventEnum.PaymentConfirmClickDefault);

      setPayAs(PaymentMethodTypeEnum.CreditCard);
    };

    const onClickOffline = () => {
      firebase
        .analytics()
        .logEvent(AnalyticsEventEnum.PaymentConfirmClickOthers);

      setPayAs(PaymentMethodTypeEnum.Offline);
    };

    return (
      <JoinSubscriptionBoxView>
        <div className="w-full items-center mb-4">
          <Heading.H5 className="text-2xl">
            {t("subscriptionPlan:payByMethod")}
          </Heading.H5>
        </div>

        <div className="w-full flex flex-col space-y-2 md:space-y-0 md:flex-row md:space-x-2">
          <div className="flex-1">
            <JoinSubscriptionPaymentButton
              active={showDefault}
              onClick={onClickCreditCard}
              label={t("creditCardPaymentMethod")}
            />
          </div>
          {isOfflinePaymentEnabled && (
            <div className="flex-1">
              <JoinSubscriptionPaymentButton
                active={!showDefault}
                onClick={onClickOffline}
                label={t("offlinePaymentMethod")}
              />
            </div>
          )}
        </div>

        {!isOfflinePaymentEnabled || showDefault ? (
          /* Stripe Payment */
          <JoinSubscriptionByStripeView
            subscriptionPlanId={subscriptionPlanId}
            postId={postId}
            onCancel={onCancel}
            isUnlockExpiredPost={isUnlockExpiredPost}
          />
        ) : (
          /* Offline Payment */
          <JoinSubscriptionByCreditView
            subscriptionPlanId={subscriptionPlanId}
            onClose={onCancel}
            price={price}
            isUnlockExpiredPost={isUnlockExpiredPost}
            postId={postId}
          />
        )}
      </JoinSubscriptionBoxView>
    );
  };

export const Link = ({ label, href }: { href: string; label: string }) => {
  return (
    <NextLink href={href}>
      <a target="_blank" rel="noopener noreferrer">
        <span className="text-primary">{label}</span>
      </a>
    </NextLink>
  );
};
